import {useSearch} from "@tanstack/react-router";
import {useLayoutEffect, useRef} from "react";
import {type FilterConfiguration, type FilterConfigurationLineItem} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import type {VisibleFiltersConfiguration} from "~/components/ottopilotDataViewer/filterComponentStupider";
import type {Dispatch, SetStateAction} from "react";
import {
    AMARON_MODULE_STATUS_FILTER_SCAFFOLD,
    type ModuleStatusFilterScaffold,
    type ModuleStatusNavConfig,
} from "~/domainModels/moduleStatusNav";
import {
    applyInQueueAssignmentStatusIdToFilterConfiguration,
    inQueueLeadOwnerAssignmentFilterMatchesStatusId,
} from "~/utilities/inQueueAssignmentFilters";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";
import {
    isJourneyScopeManagedFilterLineItem,
    type JourneyScopeFilterConfig,
} from "~/components/ottopilotDataViewer/journeyScopeFilterConfiguration";

/** Reads `statusId` from the browser URL (sidebar scaffold links use full hrefs). */
export function readModuleStatusIdFromBrowserLocation(): string | undefined {
    try {
        const fromUrl = new URL(window.location.href).searchParams.get("statusId");
        if (fromUrl !== null && fromUrl.trim() !== "") {
            return fromUrl.trim();
        }
    } catch {
        /* ignore */
    }
    return undefined;
}

export function resolveModuleStatusIdForFilter(args: {
    statusIdFromSearch?: string;
    validStatusIds?: ReadonlySet<string>;
    ignoreStatusIdSearchParam?: boolean;
}): string | null {
    if (args.ignoreStatusIdSearchParam === true) {
        return null;
    }
    const candidate = args.statusIdFromSearch ?? readModuleStatusIdFromBrowserLocation();
    if (candidate === undefined || candidate.trim() === "") {
        return null;
    }
    const validIds = args.validStatusIds;
    if (validIds !== undefined && validIds.has(candidate) !== true) {
        return null;
    }
    return candidate;
}

function getStatusIdFromRouterSearch(search: unknown): string | undefined {
    if (typeof search !== "object" || search === null || ("statusId" in search) !== true) {
        return undefined;
    }
    const rawStatusId = (search as {statusId?: unknown}).statusId;
    return typeof rawStatusId === "string" ? rawStatusId : undefined;
}

/** Applies sidebar module-status selection to a filter config (used on init and on URL changes). */
export function mergeModuleStatusFilterIntoFilterConfiguration(
    filterConfiguration: FilterConfiguration,
    statusNavConfig: ModuleStatusNavConfig | null,
    statusId: string | null,
    resolveStatusFilterValues?: (statusId: string) => ReadonlyArray<string>,
    alwaysUseInOperation = false,
): FilterConfiguration {
    if (statusNavConfig === null) {
        return filterConfiguration;
    }
    return applyModuleStatusIdToFilterConfiguration(
        filterConfiguration,
        statusNavConfig.statusColumnId,
        statusId,
        resolveStatusFilterValues,
        alwaysUseInOperation,
    );
}

function isModuleStatusFilterLineItem(
    lineItem: FilterConfigurationLineItem,
    statusColumnId: string,
): boolean {
    return (
        lineItem.columnId === String(statusColumnId) &&
        (lineItem.operation === FilterOperation.equals || lineItem.operation === FilterOperation.in)
    );
}

function removeModuleStatusFilter(
    filterConfiguration: FilterConfiguration,
    statusColumnId: string,
): Array<FilterConfigurationLineItem> {
    return filterConfiguration.lineItems.filter(
        (lineItem) => isModuleStatusFilterLineItem(lineItem, statusColumnId) !== true,
    );
}

function getModuleStatusFilterLineItem(
    filterConfiguration: FilterConfiguration,
    statusColumnId: string,
): FilterConfigurationLineItem | undefined {
    return filterConfiguration.lineItems.find((item) =>
        isModuleStatusFilterLineItem(item, statusColumnId),
    );
}

function sortedStringValues(values: ReadonlyArray<string>): Array<string> {
    return [...values].map((value) => String(value)).sort();
}

function moduleStatusFilterMatches(
    filterConfiguration: FilterConfiguration,
    statusColumnId: string,
    statusId: string | null,
    resolveStatusFilterValues?: (statusId: string) => ReadonlyArray<string>,
): boolean {
    const lineItem = getModuleStatusFilterLineItem(filterConfiguration, statusColumnId);
    if (statusId === null) {
        return lineItem === undefined;
    }
    if (lineItem === undefined) {
        return false;
    }
    const expectedValues = resolveStatusFilterValues?.(statusId) ?? [statusId];
    if (lineItem.operation === FilterOperation.in) {
        const actual = sortedStringValues(lineItem.values);
        const expected = sortedStringValues(expectedValues);
        return (
            actual.length === expected.length &&
            actual.every((value, index) => value === expected[index])
        );
    }
    if (lineItem.operation === FilterOperation.equals) {
        return String(lineItem.value) === String(expectedValues[0]);
    }
    return false;
}

export function applyModuleStatusIdToFilterConfiguration(
    filterConfiguration: FilterConfiguration,
    statusColumnId: string,
    statusId: string | null,
    resolveStatusFilterValues?: (statusId: string) => ReadonlyArray<string>,
    alwaysUseInOperation = false,
): FilterConfiguration {
    const baseLineItems = removeModuleStatusFilter(filterConfiguration, statusColumnId);
    if (statusId === null) {
        return {
            revisionId: generateUuid(),
            lineItems: baseLineItems,
        };
    }
    const filterValues = resolveStatusFilterValues?.(statusId) ?? [statusId];
    const statusFilterLineItem: FilterConfigurationLineItem =
        filterValues.length === 1 && alwaysUseInOperation !== true
            ? {
                  columnId: String(statusColumnId),
                  operation: FilterOperation.equals,
                  value: String(filterValues[0]),
              }
            : {
                  columnId: String(statusColumnId),
                  operation: FilterOperation.in,
                  values: filterValues.map((value) => String(value)),
              };
    return {
        revisionId: generateUuid(),
        lineItems: [...baseLineItems, statusFilterLineItem],
    };
}

export function useSyncModuleStatusFilterFromSearch({
    statusColumnId,
    setFilterConfiguration,
    enabled,
    validStatusIds,
    resolveStatusFilterValues,
    alwaysUseInOperation = false,
    statusIdFromSearch: statusIdFromSearchProp,
    ignoreStatusIdSearchParam = false,
    journeyScopeFilterConfig,
}: {
    statusColumnId: string | null;
    setFilterConfiguration: (
        updater: (prev: FilterConfiguration) => FilterConfiguration,
    ) => void;
    enabled: boolean;
    validStatusIds?: ReadonlySet<string>;
    /** Maps sidebar status id to API filter value(s), e.g. Active -> [activeUuid, liveUuid]. */
    resolveStatusFilterValues?: (statusId: string) => ReadonlyArray<string>;
    /** Amaron status pills use `in` filters so the checkbox UI reflects the active sidebar slice. */
    alwaysUseInOperation?: boolean;
    /** Prefer route `useSearch()`; falls back to `window.location` when omitted. */
    statusIdFromSearch?: string;
    ignoreStatusIdSearchParam?: boolean;
    /** When set, do not strip Display-by filters when sidebar statusId is cleared. */
    journeyScopeFilterConfig?: JourneyScopeFilterConfig;
}): void {
    const search = useSearch({strict: false});
    const statusIdFromRouterSearch = getStatusIdFromRouterSearch(search);
    const validStatusIdsRef = useRef(validStatusIds);
    validStatusIdsRef.current = validStatusIds;
    const resolveStatusFilterValuesRef = useRef(resolveStatusFilterValues);
    resolveStatusFilterValuesRef.current = resolveStatusFilterValues;
    const alwaysUseInOperationRef = useRef(alwaysUseInOperation);
    alwaysUseInOperationRef.current = alwaysUseInOperation;
    const journeyScopeFilterConfigRef = useRef(journeyScopeFilterConfig);
    journeyScopeFilterConfigRef.current = journeyScopeFilterConfig;

    useLayoutEffect(() => {
        if (enabled !== true || statusColumnId === null) {
            return;
        }

        const resolvedStatusId = resolveModuleStatusIdForFilter({
            statusIdFromSearch: statusIdFromSearchProp ?? statusIdFromRouterSearch,
            validStatusIds: validStatusIdsRef.current,
            ignoreStatusIdSearchParam: ignoreStatusIdSearchParam,
        });

        setFilterConfiguration((prev) => {
            if (resolvedStatusId === null) {
                const lineItem = getModuleStatusFilterLineItem(prev, statusColumnId);
                if (lineItem === undefined) {
                    return prev;
                }
                const journeyScopeConfig = journeyScopeFilterConfigRef.current;
                if (
                    journeyScopeConfig !== undefined &&
                    isJourneyScopeManagedFilterLineItem(lineItem, journeyScopeConfig) === true
                ) {
                    return prev;
                }
            }

            const legacyEqualsStatusFilter =
                alwaysUseInOperationRef.current === true &&
                resolvedStatusId !== null &&
                getModuleStatusFilterLineItem(prev, statusColumnId)?.operation === FilterOperation.equals;
            if (
                legacyEqualsStatusFilter !== true &&
                moduleStatusFilterMatches(
                    prev,
                    statusColumnId,
                    resolvedStatusId,
                    resolveStatusFilterValuesRef.current,
                ) === true
            ) {
                return prev;
            }
            return applyModuleStatusIdToFilterConfiguration(
                prev,
                statusColumnId,
                resolvedStatusId,
                resolveStatusFilterValuesRef.current,
                alwaysUseInOperationRef.current,
            );
        });
    }, [
        enabled,
        ignoreStatusIdSearchParam,
        alwaysUseInOperation,
        journeyScopeFilterConfig,
        setFilterConfiguration,
        statusColumnId,
        statusIdFromSearchProp,
        statusIdFromRouterSearch,
    ]);
}

/** In Queue: sync sidebar Assigned/Unassigned to `leadOwnerColumnId` null / not-null API filters. */
export function useSyncInQueueAssignmentFilterFromSearch({
    leadOwnerColumnId,
    setFilterConfiguration,
    enabled,
    validStatusIds,
    statusIdFromSearch: statusIdFromSearchProp,
    ignoreStatusIdSearchParam = false,
}: {
    leadOwnerColumnId: string | null;
    setFilterConfiguration: (
        updater: (prev: FilterConfiguration) => FilterConfiguration,
    ) => void;
    enabled: boolean;
    validStatusIds?: ReadonlySet<string>;
    statusIdFromSearch?: string;
    ignoreStatusIdSearchParam?: boolean;
}): void {
    const search = useSearch({strict: false});
    const statusIdFromRouterSearch = getStatusIdFromRouterSearch(search);
    const validStatusIdsRef = useRef(validStatusIds);
    validStatusIdsRef.current = validStatusIds;

    useLayoutEffect(() => {
        if (enabled !== true || leadOwnerColumnId === null) {
            return;
        }

        const resolvedStatusId = resolveModuleStatusIdForFilter({
            statusIdFromSearch: statusIdFromSearchProp ?? statusIdFromRouterSearch,
            validStatusIds: validStatusIdsRef.current,
            ignoreStatusIdSearchParam: ignoreStatusIdSearchParam,
        });

        setFilterConfiguration((prev) => {
            if (
                inQueueLeadOwnerAssignmentFilterMatchesStatusId(
                    prev,
                    leadOwnerColumnId,
                    resolvedStatusId,
                ) === true
            ) {
                return prev;
            }
            return applyInQueueAssignmentStatusIdToFilterConfiguration(
                prev,
                leadOwnerColumnId,
                resolvedStatusId,
            );
        });
    }, [
        enabled,
        ignoreStatusIdSearchParam,
        leadOwnerColumnId,
        setFilterConfiguration,
        statusIdFromSearchProp,
        statusIdFromRouterSearch,
    ]);
}

/** Removes status filter pills from the toolbar when a sidebar status child view is active. */
export function useHideModuleStatusFilterUiInChildView({
    enabled,
    statusNavConfig,
    setVisibleFilters,
    validStatusIds,
    statusIdFromSearch: statusIdFromSearchProp,
    ignoreStatusIdSearchParam = false,
    moduleStatusFilterScaffold,
}: {
    enabled: boolean;
    statusNavConfig: ModuleStatusNavConfig | null;
    setVisibleFilters: Dispatch<SetStateAction<VisibleFiltersConfiguration>>;
    validStatusIds?: ReadonlySet<string>;
    statusIdFromSearch?: string;
    ignoreStatusIdSearchParam?: boolean;
    moduleStatusFilterScaffold: ModuleStatusFilterScaffold;
}): void {
    const statusIdFromRouterSearch = getStatusIdFromRouterSearch(useSearch({strict: false}));

    useLayoutEffect(() => {
        if (enabled !== true || statusNavConfig === null) {
            return;
        }

        const activeStatusId = resolveModuleStatusIdForFilter({
            statusIdFromSearch: statusIdFromSearchProp ?? statusIdFromRouterSearch,
            validStatusIds,
            ignoreStatusIdSearchParam,
        });

        if (moduleStatusFilterScaffold.isModuleStatusChildView({statusNavConfig, activeStatusId}) !== true) {
            return;
        }

        const hiddenColumnIds = moduleStatusFilterScaffold.getModuleStatusFilterColumnIds(statusNavConfig);
        setVisibleFilters((prev) => {
            const nextLineItems = prev.lineItems.filter(
                (columnId) => hiddenColumnIds.has(String(columnId)) !== true,
            );
            if (nextLineItems.length === prev.lineItems.length) {
                return prev;
            }
            return {
                revisionId: generateUuid(),
                lineItems: nextLineItems,
            };
        });
    }, [
        enabled,
        ignoreStatusIdSearchParam,
        moduleStatusFilterScaffold,
        setVisibleFilters,
        statusIdFromSearchProp,
        statusIdFromRouterSearch,
        statusNavConfig,
        validStatusIds,
    ]);
}

/** Removes status filter pills from the toolbar in Amaron sidebar status child views. */
export function useHideAmaronModuleStatusFilterUiInChildView(
    args: Omit<Parameters<typeof useHideModuleStatusFilterUiInChildView>[0], "moduleStatusFilterScaffold">,
): void {
    useHideModuleStatusFilterUiInChildView({
        ...args,
        moduleStatusFilterScaffold: AMARON_MODULE_STATUS_FILTER_SCAFFOLD,
    });
}
